/**
 * Sexy Hair theme-owned forms, gated by Friendly Captcha (WEB-6277, on the
 * DEV-4214 engine).
 *
 * A PORT of `assets/kenra-form.js`. The protocol, the error handling and the
 * captcha lifecycle are that file's; three values and one selector are this
 * brand's, and they are called out below.
 *
 * Posts JSON to the app proxy. The server is what actually enforces the captcha
 * — everything here is convenience for the visitor. Disabling the submit button
 * until the widget completes stops honest people wasting a round trip; it is not
 * the control, because a bot never runs this file.
 */
(function () {
  'use strict';

  // The brand-NEUTRAL app-proxy path, which is `app/routes/forms.submit.ts`
  // re-exporting the same action Kenra's own path uses. Kenra keeps
  // `/apps/extensions/kenra-forms/submit` only because that path is baked into
  // its LIVE theme and cannot be renamed. Do not point this at Kenra's path.
  var ENDPOINT = '/apps/extensions/forms/submit';
  var CAPTCHA_FIELD = 'frc-captcha-response';
  // Must match `forms.honeypotField` for THIS brand in brands.json, which the
  // server reads through app/utils/forms-profile.ts. Hardcoding Kenra's
  // `kenra_hp` here would make every field look unexpected to the validator and
  // refuse every submission.
  var HONEYPOT_FIELD = 'sexyhair_hp';
  // The checkbox value that reveals a free-text box. Unused by the salon request
  // form, which has one fixed option; kept because the engine supports it.
  var OTHER_OPTION = 'Other';

  // The submit button is the shared design-system CTA
  // (`snippets/sexyhair-cta-button.liquid`), which renders a fixed set of
  // attributes and takes no passthrough — so the hook is a CLASS rather than
  // Kenra's `[data-kenra-form-submit]`. Renaming it means editing
  // `snippets/sexyhair-form.liquid` in the same commit.
  var SUBMIT_SELECTOR = '.sexyhair-form__submit';
  var DISABLED_CLASS = 'sexyhair-cta--disabled';

  /**
   * Disable or enable the submit button, keeping the CTA's disabled CLASS in
   * step with the `disabled` attribute.
   *
   * This is NOT the WEB-6148 buy-button trap, where the class was the only
   * thing carrying the grey look so a missed toggle left a dead-looking
   * control. `assets/sexyhair-component-cta.css` styles `.sexyhair-cta:disabled`
   * directly beside `.sexyhair-cta--disabled`, so the attribute alone is already
   * correct and the class is belt and braces. It is synced anyway because the
   * snippet emits both, and a button whose class and state disagree is a trap
   * for whoever reads the DOM next.
   */
  function setDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
    button.classList.toggle(DISABLED_CLASS, disabled);
  }

  /**
   * Translated strings ride in on the form's data attributes, put there by
   * snippets/sexyhair-form.liquid. The fallback only applies if that markup and
   * this file ever drift apart.
   */
  function t(form, key, fallback) {
    var value = form.dataset[key];
    return value && value.length > 0 ? value : fallback;
  }

  function clearErrors(form) {
    form.querySelectorAll('.sexyhair-form__error').forEach(function (node) {
      node.remove();
    });
    form.querySelectorAll('[data-field]').forEach(function (node) {
      node.classList.remove('sexyhair-form__field--invalid');
    });
    // showFieldErrors sets these on the inputs. Removing the message nodes is
    // not enough: a screen reader would keep announcing the field as invalid,
    // and aria-describedby would point at an id that no longer exists.
    form.querySelectorAll('[aria-invalid]').forEach(function (node) {
      node.removeAttribute('aria-invalid');
    });
    // Remove ONLY the association this script added. showFieldErrors points the
    // input at its own message id, which always starts 'sexyhair-error-'. Stripping
    // every aria-describedby would also remove unrelated help-text associations
    // the moment help text starts using one.
    form.querySelectorAll('[aria-describedby]').forEach(function (node) {
      var ids = (node.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      var kept = ids.filter(function (id) {
        return id.indexOf('sexyhair-error-') !== 0;
      });
      if (kept.length === ids.length) return;
      if (kept.length > 0) node.setAttribute('aria-describedby', kept.join(' '));
      else node.removeAttribute('aria-describedby');
    });
    var summary = form.querySelector('[data-sexyhair-form-summary]');
    if (summary) {
      summary.hidden = true;
      summary.textContent = '';
    }
  }

  function showSummary(form, message) {
    var summary = form.querySelector('[data-sexyhair-form-summary]');
    if (!summary) return;
    summary.textContent = message;
    summary.hidden = false;
    summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Paint the server's field errors next to their inputs.
   *
   * Returns the messages that have no field on the page — the server sends
   * `_form` for an unreadable body and for unexpected fields, and there is no
   * `[data-field="_form"]` element to hang those on. The caller shows them in
   * the summary instead, so the visitor is never told to check highlighted
   * fields when nothing is highlighted.
   */
  function showFieldErrors(form, errors) {
    var firstInvalid = null;
    var unmatched = [];

    Object.keys(errors).forEach(function (fieldName) {
      var wrapper = form.querySelector('[data-field="' + fieldName + '"]');
      if (!wrapper) {
        if (errors[fieldName]) unmatched.push(errors[fieldName]);
        return;
      }

      wrapper.classList.add('sexyhair-form__field--invalid');

      var message = document.createElement('p');
      message.className = 'sexyhair-form__error type-body-sm';
      message.textContent = errors[fieldName];
      wrapper.appendChild(message);

      var input = wrapper.querySelector('input, textarea, select');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        // Point the input at its message so a screen reader reads them together.
        var messageId = 'sexyhair-error-' + fieldName + '-' + (input.id || fieldName);
        message.id = messageId;
        // APPEND. aria-describedby is a space-separated LIST of ids, and the
        // input may already name its help text. Overwriting destroyed that
        // association permanently after one failed submission — clearErrors
        // then removed the value it found, and the original id was gone.
        var described = (input.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter(Boolean);
        if (described.indexOf(messageId) === -1) described.push(messageId);
        input.setAttribute('aria-describedby', described.join(' '));
      }

      if (!firstInvalid) firstInvalid = input || wrapper;
    });

    if (firstInvalid && typeof firstInvalid.focus === 'function') {
      firstInvalid.focus();
    }

    return unmatched;
  }

  /**
   * Ask the Friendly Captcha widget for a new token.
   *
   * `reset()` lives on the WIDGET HANDLE, not on `window.frcaptcha` — the
   * manager only exposes attach/createWidget/getAllWidgets/getWidgetById/clear.
   * Calling `window.frcaptcha.reset()` is a silent no-op, which left the spent
   * token in place and locked the visitor out after any failed submission.
   */
  function resetCaptcha(form) {
    var widget = form.querySelector('.frc-captcha');

    // The SDK hangs the handle off the element itself. getWidgetById takes the
    // SDK's OWN internal id ("w_" + random), not our element id, so passing the
    // element id there always missed and fell through to resetting every widget
    // on the page.
    if (widget && widget.frcWidget && typeof widget.frcWidget.reset === 'function') {
      widget.frcWidget.reset();
      return;
    }

    // Only reached if the SDK changes shape. Resets every widget on the page,
    // which is heavy-handed but better than leaving a spent token in place.
    if (window.frcaptcha && typeof window.frcaptcha.getAllWidgets === 'function') {
      window.frcaptcha.getAllWidgets().forEach(function (each) {
        if (each && typeof each.reset === 'function') each.reset();
      });
    }
  }

  /**
   * Read the form by field wrapper rather than by input, because the shape of a
   * value depends on the field type declared in the sexyhair_form metaobject:
   * a multi_checkbox yields an array, everything else yields a string.
   *
   * A radio group holds one input per option, so its value is the CHECKED one,
   * or '' when none is — never the first input's value, which the generic
   * branch below would read (WEB-6283).
   *
   * Wrappers with no data-field-type are skipped. The captcha block is one of
   * them — its token travels as captchaResponse, not inside fields.
   */
  function collectFields(form) {
    var fields = {};

    form.querySelectorAll('[data-field][data-field-type]').forEach(function (wrapper) {
      var name = wrapper.getAttribute('data-field');
      var type = wrapper.getAttribute('data-field-type');
      if (!name) return;

      if (type === 'multi_checkbox') {
        var checked = wrapper.querySelectorAll('input[type="checkbox"]:checked');
        var otherInput = wrapper.querySelector('.sexyhair-form__other-input');
        fields[name] = Array.prototype.map.call(checked, function (input) {
          // Parity with Hulk: the "Other" tick carries its free text with it,
          // as "Other(<text>)". The server accepts that shape only when the
          // field declares allowOther.
          if (otherInput && input.value === OTHER_OPTION) {
            var typed = otherInput.value.trim();
            if (typed) return OTHER_OPTION + '(' + typed + ')';
          }
          return input.value;
        });
        return;
      }

      if (type === 'radio') {
        var picked = wrapper.querySelector('input[type="radio"]:checked');
        fields[name] = picked ? picked.value : '';
        return;
      }

      var input = wrapper.querySelector('input, textarea, select');
      if (input) fields[name] = input.value;
    });

    // The honeypot sits outside the field wrappers. The server discards any
    // submission that carries a value here.
    var honeypot = form.querySelector('input[name="' + HONEYPOT_FIELD + '"]');
    if (honeypot) fields[HONEYPOT_FIELD] = honeypot.value;

    return fields;
  }

  /**
   * The optional `data-quiz-result` payload, or null (WEB-6283, a port of
   * WEB-6550's readQuizResult in kenra-form.js).
   *
   * Parsed rather than forwarded as a string so a malformed attribute is
   * dropped HERE, where it costs nothing, instead of being posted for the
   * server to refuse — a refusal there discards the shopper's whole quizResult.
   * Anything that is not a plain object is treated as absent.
   */
  function readQuizResult(form) {
    var raw = form.getAttribute('data-quiz-result');
    if (!raw) return null;
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_e) {
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || Object.prototype.toString.call(parsed) === '[object Array]') {
      return null;
    }
    return parsed;
  }

  function getCaptchaResponse(form) {
    var field = form.querySelector('[name="' + CAPTCHA_FIELD + '"]');
    return field ? field.value : '';
  }

  function setBusy(form, busy) {
    var button = form.querySelector(SUBMIT_SELECTOR);
    if (!button) return;
    setDisabled(button, busy);
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) {
      button.dataset.idleLabel = button.textContent;
      button.textContent = t(form, 'i18nSubmitting', 'Submitting…');
    } else if (button.dataset.idleLabel) {
      button.textContent = button.dataset.idleLabel;
    }
  }

  function succeed(form) {
    var root = form.closest('.sexyhair-form');
    var success = root ? root.querySelector('[data-sexyhair-form-success]') : null;
    form.hidden = true;
    if (success) {
      success.hidden = false;
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var heading = success.querySelector('h2');
      if (heading) {
        // Move focus so the confirmation is announced and the page does not
        // strand keyboard users on a hidden form.
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }
  }

  function initForm(form) {
    if (form.dataset.sexyhairFormReady === 'true') return;
    form.dataset.sexyhairFormReady = 'true';

    var button = form.querySelector(SUBMIT_SELECTOR);

    // Set after a refused submission. The token is spent, so the button must
    // stay shut until the widget hands us a fresh one, whatever setBusy does.
    var lockUntilNewToken = false;

    // Keep submission shut until the widget has produced a token. If the SDK
    // never loads, the button stays disabled and the visitor sees the pending
    // message rather than a submission the server would refuse anyway.
    if (button) setDisabled(button, true);

    form.addEventListener('frc:widget.complete', function () {
      lockUntilNewToken = false;
      if (button) setDisabled(button, false);
    });

    ['frc:widget.error', 'frc:widget.expire', 'frc:widget.reset'].forEach(function (eventName) {
      form.addEventListener(eventName, function () {
        if (button) setDisabled(button, true);
      });
    });

    // Reveal the "Other" free-text box only while its checkbox is ticked.
    form.querySelectorAll('[data-sexyhair-other-for]').forEach(function (other) {
      var fieldName = other.getAttribute('data-sexyhair-other-for');
      var group = form.querySelector('[data-field="' + fieldName + '"]');
      if (!group) return;

      var sync = function () {
        var box = group.querySelector('input[type="checkbox"][value="' + OTHER_OPTION + '"]');
        other.hidden = !(box && box.checked);
      };
      group.addEventListener('change', sync);
      sync();
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors(form);

      // The live form makes this box required once "Other" is ticked. Check it
      // here, because the server cannot tell an empty box from an unticked one.
      var missingOther = null;
      form.querySelectorAll('[data-sexyhair-other-for]').forEach(function (other) {
        if (other.hidden || missingOther) return;
        var typed = other.querySelector('.sexyhair-form__other-input');
        if (typed && typed.value.trim() === '') missingOther = typed;
      });
      if (missingOther) {
        showSummary(form, t(form, 'i18nOtherRequired', 'Please name your other option.'));
        missingOther.focus();
        return;
      }

      var captchaResponse = getCaptchaResponse(form);
      if (!captchaResponse) {
        showSummary(form, t(form, 'i18nCaptchaPending', 'Please complete the security check.'));
        return;
      }

      setBusy(form, true);

      var payload = {
        formKey: form.dataset.formKey,
        locale: form.dataset.locale || '',
        fields: collectFields(form),
        captchaResponse: captchaResponse,
      };

      // An optional enrichment another script may have attached (WEB-6283):
      // assets/sexyhair-quiz.js writes the shopper's answers, the quiz key and
      // the results link here. The route stores them on the submission, where
      // the Attentive tooling (WEB-6562) reads them for the results email.
      //
      // This file serves EVERY Sexy Hair form and must not know what a quiz is,
      // so it reads one opaque attribute and forwards it unexamined. The server
      // validates every key and value — see kenra-quiz-payload.server.ts — and
      // a form with no such attribute sends no such key.
      var quizResult = readQuizResult(form);
      if (quizResult) payload.quizResult = quizResult;

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              return { status: response.status, body: body };
            });
        })
        .then(function (result) {
          if (result.status === 200 && result.body.ok) {
            succeed(form);
            return;
          }

          if (result.body && result.body.errors) {
            var unmatched = showFieldErrors(form, result.body.errors);
            showSummary(
              form,
              unmatched.length > 0
                ? unmatched.join(' ')
                : t(form, 'i18nErrorSummary', 'Please check the highlighted fields.')
            );
          } else {
            showSummary(
              form,
              (result.body && result.body.error) ||
                t(form, 'i18nErrorGeneric', 'Something went wrong. Please try again.')
            );
          }

          // The token is single use — a replay is refused as response_duplicate,
          // so the visitor must solve the widget again before retrying. The
          // server checks the captcha BEFORE it validates the fields, so an
          // ordinary mistake (a missed required field) also spends the token.
          resetCaptcha(form);
          lockUntilNewToken = true;
        })
        .catch(function () {
          showSummary(form, t(form, 'i18nErrorGeneric', 'Something went wrong. Please try again.'));
          resetCaptcha(form);
          lockUntilNewToken = true;
        })
        .finally(function () {
          setBusy(form, false);
          // setBusy re-enables the button. Re-apply the lock afterwards, or the
          // visitor can resubmit with the token they have already spent.
          if (lockUntilNewToken && button) setDisabled(button, true);
        });
    });
  }

  function init() {
    document.querySelectorAll('[data-sexyhair-form]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections can be added or moved in the theme editor without a page reload.
  // The SDK auto-attaches once per page load, so a section added afterwards
  // gets our handlers and a widget that never mounts. Ask the SDK to attach
  // again; without this the submit button stays disabled forever in the editor.
  document.addEventListener('shopify:section:load', function () {
    init();
    if (window.frcaptcha && typeof window.frcaptcha.attach === 'function') {
      window.frcaptcha.attach();
    }
  });
})();
